import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { allArticles } from '@/mocks/articles';

export default function Navbar() {
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileExpandedCat, setMobileExpandedCat] = useState(false);
  const [mobileExpandedGuide, setMobileExpandedGuide] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
        setIsCategoryOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(event.target as Node)) {
        setIsGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    setIsCategoryOpen(false);
    setIsMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  const handleGuideSelect = (slug: string) => {
    setIsGuideOpen(false);
    setIsMobileMenuOpen(false);
    setMobileExpandedGuide(false);
    navigate(`/article/${slug}`);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 whitespace-nowrap">
              Vigorbuy Spread
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors duration-200 cursor-pointer whitespace-nowrap font-medium"
            >
              <i className="ri-home-4-line w-4 h-4 inline-flex items-center justify-center align-middle mr-1"></i>
              Home
            </Link>

            <div className="relative" ref={categoryRef}>
              <button
                onClick={() => {
                  setIsCategoryOpen(!isCategoryOpen);
                  setIsGuideOpen(false);
                }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                Categories
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isCategoryOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[580px] bg-background-50 rounded-xl border border-background-200 shadow-lg overflow-hidden z-50">
                  <div className="grid grid-cols-2 gap-1 p-2 max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategorySelect(cat.url)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-background-100 text-left transition-colors duration-150 cursor-pointer group whitespace-nowrap"
                      >
                        <span className="w-7 h-7 rounded-md bg-background-100 group-hover:bg-background-200 flex items-center justify-center flex-shrink-0">
                          <i className="ri-arrow-right-up-line text-xs text-foreground-500"></i>
                        </span>
                        <span className="text-sm text-foreground-800 group-hover:text-foreground-950 font-medium">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={guideRef}>
              <button
                onClick={() => {
                  setIsGuideOpen(!isGuideOpen);
                  setIsCategoryOpen(false);
                }}
                className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors duration-200 cursor-pointer whitespace-nowrap font-medium"
              >
                <i className="ri-book-open-line w-4 h-4 inline-flex items-center justify-center align-middle mr-0.5"></i>
                Shopping Guide
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isGuideOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isGuideOpen && (
                <div className="absolute top-full mt-2 right-0 w-[380px] bg-background-50 rounded-xl border border-background-200 shadow-lg overflow-hidden z-50">
                  <div className="p-2 max-h-[480px] overflow-y-auto">
                    <p className="px-3 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Vigorbuy Spreadsheet Guides</p>
                    {allArticles.map((article) => (
                      <button
                        key={article.slug}
                        onClick={() => handleGuideSelect(article.slug)}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer group"
                      >
                        <div className="flex items-start gap-2.5">
                          <span className="w-8 h-8 rounded-md bg-background-100 group-hover:bg-background-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-article-line text-xs text-foreground-500"></i>
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm text-foreground-800 group-hover:text-foreground-950 font-medium leading-snug line-clamp-1">
                              {article.title}
                            </p>
                            <p className="text-xs text-foreground-400 mt-0.5">{article.category} · {article.readTime}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Visit Main Website
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-3 space-y-2 max-h-[calc(100dvh-4rem)] overflow-y-auto">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-home-4-line text-primary-500"></i>
              </span>
              <span className="text-sm font-semibold text-foreground-800">Home</span>
            </Link>

            <button
              onClick={() => setMobileExpandedCat(!mobileExpandedCat)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-grid-line text-secondary-500"></i>
                </span>
                <span className="text-sm font-semibold text-foreground-800">Categories</span>
              </span>
              <i className={`ri-arrow-down-s-line text-foreground-400 w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileExpandedCat ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileExpandedCat && (
              <div className="ml-3 pl-3 border-l-2 border-background-200 space-y-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer text-left"
                  >
                    <span className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                    </span>
                    <span className="text-sm text-foreground-700">{cat.name}</span>
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => setMobileExpandedGuide(!mobileExpandedGuide)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-book-open-line text-accent-500"></i>
                </span>
                <span className="text-sm font-semibold text-foreground-800">Shopping Guide</span>
              </span>
              <i className={`ri-arrow-down-s-line text-foreground-400 w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileExpandedGuide ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileExpandedGuide && (
              <div className="ml-3 pl-3 border-l-2 border-background-200 space-y-1">
                {allArticles.map((article) => (
                  <button
                    key={article.slug}
                    onClick={() => handleGuideSelect(article.slug)}
                    className="w-full flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer text-left"
                  >
                    <span className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-article-line text-xs text-foreground-400"></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-700 font-medium leading-snug line-clamp-1">{article.title}</p>
                      <p className="text-xs text-foreground-400 mt-0.5">{article.category} · {article.readTime}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="border-t border-background-200 pt-2 mt-1">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer justify-center"
              >
                Visit Main Website
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}